import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Download, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader 
} from '@/components/layout/MobileLayout';
import { mockTickets } from '@/lib/mockData';
import { formatDate, formatTime } from '@/lib/utils';

export function UserTicketQR() {
  const { ticketId } = useParams();
  
  const ticket = mockTickets.find(t => t.id === ticketId) || mockTickets[0];

  return (
    <div className="min-h-screen bg-background" data-testid="user-ticket-qr">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />
      
      <main className="pt-14 lg:pt-20 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 max-w-md">
          {/* Back button */}
          <Link to={`/me/tickets/${ticketId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Ticket
          </Link>

          <Card className="bg-card border-border overflow-hidden text-center">
            <div className="p-6 space-y-6">
              {/* Event Title */}
              <div>
                <h1 className="text-xl font-bold text-white mb-1">{ticket.eventTitle}</h1>
                <p className="text-primary font-medium">{ticket.ticketType}</p>
              </div>

              {/* QR Code */}
              <div className="bg-white p-6 rounded-xl mx-auto max-w-[250px]">
                {/* Simulated QR Code */}
                <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="grid grid-cols-8 gap-1 p-4">
                    {Array.from({ length: 64 }).map((_, i) => (
                      <div 
                        key={i} 
                        className={`w-3 h-3 rounded-sm ${(i * 7 + i % 3) % 2 === 0 ? 'bg-black' : 'bg-white'}`}
                      />
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-primary-foreground font-bold">88</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket Code */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Ticket Code</p>
                <p className="text-lg font-mono font-bold text-white">{ticket.qrCode}</p>
              </div>

              {/* Event Details */}
              <div className="bg-secondary/50 rounded-lg p-4">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span className="text-white">{formatDate(ticket.eventDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time</span>
                    <span className="text-white">{formatTime(ticket.eventDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Venue</span>
                    <span className="text-white">{ticket.venue}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quantity</span>
                    <span className="text-white">{ticket.quantity} ticket(s)</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
                  <Download className="w-4 h-4 mr-2" />
                  Save to Device
                </Button>
                <Button variant="outline" className="border-border">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                Present this QR code at the venue entrance for check-in
              </p>
            </div>
          </Card>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export default UserTicketQR;
